import { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { loadBrandKitAsync } from "./brandKitSlice";
import { SIDEBAR_BRANDING_PERMISSIONS } from "./sidebar/sidebarBranding.settingsTab";

import { usePermission } from "@/auth/permissions/usePermission";
import type { AppDispatch, RootState } from "@/redux/store";

// Mirrors src/features/tenantBranding/TenantBrandingBootstrap.tsx exactly.
export function BrandKitBootstrap() {
  const dispatch = useDispatch<AppDispatch>();
  const { can } = usePermission();
  // GET /brand-kit requires this key; teachers and guardians do not hold it by default.
  const canReadBrandKit = can(SIDEBAR_BRANDING_PERMISSIONS.read);
  const {
    initialized,
    isAuthenticated,
    tenantId,
    currentTenantId,
    loading,
    currentExpiresAt,
    failedTenantId,
  } = useSelector(
    (state: RootState) => ({
      initialized: state.auth.initialized,
      isAuthenticated: state.auth.isAuthenticated,
      tenantId: state.auth.user?.tenantId ?? null,
      currentTenantId: state.brandKit.currentTenantId,
      loading: state.brandKit.loading,
      currentExpiresAt: state.auth.user?.tenantId
        ? (state.brandKit.cache[state.auth.user.tenantId]?.expiresAt ?? null)
        : null,
      failedTenantId: state.brandKit.failedTenantId,
    }),
    shallowEqual,
  );

  useEffect(() => {
    // Without the permission the request can only answer 403, which surfaced as a "Not authorized" toast on every
    // app load for every teacher and parent. They keep the default theme, exactly as before.
    if (!initialized || !isAuthenticated || !tenantId || !canReadBrandKit) {
      return;
    }

    if (loading && currentTenantId === tenantId) {
      return;
    }

    if (
      currentTenantId === tenantId &&
      currentExpiresAt &&
      Date.parse(currentExpiresAt) > Date.now()
    ) {
      return;
    }

    // The last attempt for this tenant failed — don't auto-retry every render (that
    // starves the tab in an unbounded request loop). A fresh attempt only happens for
    // a different tenant, or after something explicitly clears the failure (reload,
    // re-login, a user-triggered retry).
    if (failedTenantId === tenantId) {
      return;
    }

    dispatch(loadBrandKitAsync({ tenantId }));
  }, [
    canReadBrandKit,
    currentExpiresAt,
    currentTenantId,
    dispatch,
    failedTenantId,
    initialized,
    isAuthenticated,
    loading,
    tenantId,
  ]);

  return null;
}
